/**
 * Refus AVANT le clic des actions d'IA pendant l'arrêt d'urgence (§ 4.10.1 ; planches
 * `DeposerIdee`, `Specifications`, `EditeurSpecification`, `EtapesSuivantes` — état
 * « refus-avant-clic »). L'état composé est lu sur `GET /ai/kill-switch/state` (le même cache
 * que le bandeau global) ; un refus `AI_KILL_SWITCH_ACTIVE` déjà constaté vaut aussi.
 *
 * L'écran ne décide de rien : la passerelle reste seule juge. Il évite seulement d'inviter à un
 * clic dont on sait déjà qu'il sera refusé, et il dit pourquoi.
 */
import { useId, type JSX } from 'react'

import { useEtatArretIA } from '@/components/arret/useEtatArretIA'
import { useSession } from '@/hooks/useSession'
import { useT } from '@/i18n/language'

export const CODE_ARRET_IA = 'AI_KILL_SWITCH_ACTIVE'

export interface RefusIA {
  /** Vrai si une action d'IA serait refusée maintenant. */
  refuse: boolean
  /** Identifiant du motif, à poser en `aria-describedby` sur le bouton refusé. */
  idMotif: string
  /** Portée de la cause la plus large (plateforme d'abord), `null` si seulement constatée. */
  portee: 'plateforme' | 'organisation' | null
}

export function useRefusIA(): RefusIA {
  const session = useSession()
  const etat = useEtatArretIA(session?.roles ?? [])
  const idMotif = useId()
  const refuse = etat.causes.length > 0 || etat.constate !== null
  return { refuse, idMotif, portee: etat.causes[0]?.portee ?? null }
}

/** Motif visible sous le bouton refusé, avec son détail technique. */
export function MotifRefusIA({ refus, texte }: { refus: RefusIA; texte?: string }): JSX.Element | null {
  const { t } = useT()
  if (!refus.refuse) return null
  const phrase = texte ?? t('arret.refusAction', 'Les nouvelles requêtes IA sont temporairement interrompues.')
  const portee = refus.portee === 'plateforme'
    ? t('arret.refusPorteePlateforme', 'Arrêt de l’IA sur la plateforme : la génération attendra la levée.')
    : refus.portee === 'organisation'
      ? t('arret.refusPorteeOrganisation', 'Arrêt de l’IA dans votre organisation : la génération attendra la levée.')
      : null
  return (
    <div id={refus.idMotif} role="note" data-testid="refus-ia" className="space-y-1 text-sm text-[var(--color-error-text)]">
      <p>{phrase}{portee ? ` ${portee}` : ''}</p>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">{t('arret.detailTechnique', 'Détail technique')}</summary>
        <code>{CODE_ARRET_IA}</code>
      </details>
    </div>
  )
}
